import { useTranslation } from "react-i18next"
import { useLocation } from "react-router"

import { SitePath } from "@/models/route"
import { SiteBillCost } from "@/modules/site/components/site-bill-cost"
import { GridActionTooltipLink, baseColumnDef } from "@/nzds/data-grid"
import type { BillPeriod } from "@/services/core-api/types"
import { useCostColumn } from "@/utils/grid-columns/use-cost-column"
import { useMeterCodeColumn } from "@/utils/grid-columns/use-meter-code-column"
import { usePeriodColumn } from "@/utils/grid-columns/use-period-column"
import { useResourceColumn } from "@/utils/grid-columns/use-resource-column"
import { useUsageColumn } from "@/utils/grid-columns/use-usage-column"
import { useUtilityProviderColumn } from "@/utils/grid-columns/use-utility-provider-column"
import { useUrl } from "@/utils/url"

import VisibilityIcon from "@mui/icons-material/Visibility"
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro"
import { GridActionsCellItem } from "@mui/x-data-grid-pro"

/** One bill period on the site bills grid, with its meter's code. */
export interface SiteBillGridRecord extends BillPeriod {
  meter_code: string
}

interface UseSiteBillsGridColumnsOptions {
  siteId: string
}

export const useSiteBillsGridColumns = ({
  siteId,
}: UseSiteBillsGridColumnsOptions): GridColDef<SiteBillGridRecord>[] => {
  const { t } = useTranslation("common")
  const { buildSiteUrl } = useUrl()
  const { search } = useLocation()
  const costColumn = useCostColumn<SiteBillGridRecord>({
    field: "cost_major",
    renderCell: (params: GridRenderCellParams<SiteBillGridRecord>) => (
      <SiteBillCost
        cost={params.row.cost_major}
        currency={params.row.currency}
      />
    ),
    useAccountingFormatWhenNegative: false,
  })
  const meterCodeColumn = useMeterCodeColumn<SiteBillGridRecord>({
    filterable: false,
  })
  const periodColumn = usePeriodColumn<SiteBillGridRecord>()
  const resourceColumn = useResourceColumn<SiteBillGridRecord>()
  const usageColumn = useUsageColumn<SiteBillGridRecord>({
    convert: false,
    field: "raw_usage",
    precision: 2,
    unitField: "raw_usage_unit",
  })
  const utilityProviderColumn = useUtilityProviderColumn<SiteBillGridRecord>()

  const buildBillDetailPath = (billId: SiteBillGridRecord["id"]) =>
    `${buildSiteUrl(siteId, SitePath.Bills)}/${billId}${search}`

  return [
    utilityProviderColumn,
    {
      ...meterCodeColumn,
      sortable: false,
    },
    {
      ...resourceColumn,
      filterable: false,
    },
    periodColumn,
    usageColumn,
    costColumn,
    {
      ...baseColumnDef,
      align: "left",
      cellClassName: "actions",
      field: "actions",
      filterable: false,
      getActions: ({ row }) => [
        <GridActionsCellItem
          component={GridActionTooltipLink}
          icon={<VisibilityIcon color="primary" />}
          key="view-action"
          label={t("button.view")}
          showInMenu={false}
          // @ts-expect-error GridActionsCellItem does not type Link's `to` prop
          to={buildBillDetailPath(row.id)}
        />,
      ],
      headerAlign: "left",
      headerName: t("gridColumn.actions.name"),
      minWidth: 100,
      sortable: false,
      type: "actions",
      width: 100,
    },
  ]
}
